<template>
  <div class="tcu-profile">
    <div class="btn-holder">
      <button
        class="tcu-button _secondary | button secondary"
        ref="focusEl"
        @click="closeProfile"
      >
        Back to Directory
      </button>
    </div>
    <div v-if="error == true" class="tcu-callout _warning">
      <p>Profile not found.</p>
    </div>
    <template v-else>
      <script type="application/ld+json" v-html="getSchema"></script>
      <div v-if="hasImage" class="tcu-profile__photo">
        <img
          @load="onImgLoad"
          v-lazyload
          :data-url="hasImage"
          :alt="profileData.name"
          width="100%"
        />
        <spinner v-if="!imgLoaded" />
      </div>
      <div v-if="profileData.name == ''" class="tcu-profile__photo">
        <spinner />
      </div>
      <template v-if="profileData.name != ''">
        <div class="tcu-profile__identity tcu-profile__push-right">
          <h1 class="tcu-profile__name h2">
            {{ profileData.name
            }}<span v-if="profileData.honorificSuffix !== ''"
              >, {{ profileData.honorificSuffix }}</span
            >
          </h1>
          <div class="tcu-profile__title">{{ profileData.jobTitle }}</div>
          <!-- <figure v-if="profileData.pronunciation" class="tcu-profile__pronunciation">
              <figcaption>Name Pronunciation</figcaption>
              <audio
                  controls
                  :src="profileData.pronunciation">
                      Your browser does not support the
                      <code>audio</code> element.
              </audio>
          </figure> -->
        </div>
        <p class="tcu-profile__contact-info">
          <span v-if="profileData.pronouns">{{ profileData.pronouns }}</span>
          <span
            v-if="profileData.email"
            class="tcu-profile__email | tcu-profile__icon"
            ><svg
              class="tcu-email-icon"
              focusable="false"
              height="24"
              width="24"
            >
              <use
                xmlns:xlink="http://www.w3.org/1999/xlink"
                href="#icon-email-icon"
                xlink:href="#icon-email-icon"
              ></use>
            </svg>
            <a :href="'mailto:' + profileData.email">{{
              profileData.email
            }}</a></span
          >
          <span
            v-if="profileData.telephone"
            class="tcu-profile__telephone | tcu-profile__icon"
            ><svg
              class="tcu-phone-icon"
              focusable="false"
              height="24"
              width="24"
            >
              <use
                xmlns:xlink="http://www.w3.org/1999/xlink"
                href="#icon-phone-icon"
                xlink:href="#icon-phone-icon"
              ></use>
            </svg>
            {{ profileData.telephone }}</span
          >
          <span
            v-if="profileData.mapLink"
            class="tcu-profile__office | tcu-profile__icon"
          >
            <svg
              class="tcu-location-pin-icon"
              focusable="false"
              height="24"
              width="24"
            >
              <use
                xmlns:xlink="http://www.w3.org/1999/xlink"
                href="#icon-location-pin-icon"
                xlink:href="#icon-location-pin-icon"
              ></use>
            </svg>
            <a
              :href="profileData.mapLink"
              target="_blank"
              title="View location on TCU interactive map"
              >{{ profileData.office }}</a
            >
          </span>
          <span
            v-else-if="profileData.office"
            class="tcu-profile__office | tcu-profile__icon"
          >
            <svg
              class="tcu-location-pin-icon"
              focusable="false"
              height="24"
              width="24"
            >
              <use
                xmlns:xlink="http://www.w3.org/1999/xlink"
                href="#icon-location-pin-icon"
                xlink:href="#icon-location-pin-icon"
              ></use>
            </svg>
            {{ profileData.office }}
          </span>
          <span
            v-if="profileData.tcuBox"
            class="tcu-profile__tcubox | tcu-profile__icon"
            ><svg
              class="tcu-mail-icon"
              focusable="false"
              height="24"
              width="24"
            >
              <use
                xmlns:xlink="http://www.w3.org/1999/xlink"
                href="#icon-mail-icon"
                xlink:href="#icon-mail-icon"
              ></use>
            </svg>
            TCU Box {{ profileData.tcuBox }}<br />
            Fort Worth, TX 76129</span
          >
        </p>
        <div
          class="tcu-profile__affiliations"
          v-if="getProgramAffiliations.length"
        >
          <h2 class="h3">Program Affiliations</h2>
          <div class="tcu-button-group button-group">
            <a
              v-for="program in getProgramAffiliations"
              :key="program.id"
              class="tcu-button _ghost | button"
              :href="program.url"
              >{{ program.displayName }}</a
            >
          </div>
        </div>
        <div v-if="cvLink" class="tcu-profile__cv">
          <a :href="cvLink">Download CV</a>
        </div>
        <div
          v-if="
            profileData.areasOfInterest && profileData.areasOfInterest.length
          "
          class="tcu-profile__interest-tags"
        >
          <span class="tcu-profile__interest-tags-icon">
            <svg width="21" height="21" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M10.211 1.024A3.504 3.504 0 007.735 0H3.5A3.499 3.499 0 000 3.5v4.238c0 .927.367 1.82 1.024 2.476L11.3 20.488c.682.683 1.789.683 2.476 0l6.711-6.71a1.753 1.753 0 000-2.475L10.211 1.023zM4 6a2 2 0 11-.001-3.999A2 2 0 014 6z"
                fill="#6D6E71"
                fill-rule="nonzero"
              />
            </svg>
          </span>
          <ul>
            <template v-if="linkInterests">
              <li
                v-for="(interest, index) in profileData.areasOfInterest"
                :key="interest.id"
              >
                <a href="#" @click="viewSimilarInterests(interest)">{{
                  interest
                }}</a>
                <span v-if="index != profileData.areasOfInterest.length - 1">
                  /
                </span>
              </li>
            </template>
            <template v-else>
              <li
                v-for="(interest, index) in profileData.areasOfInterest"
                :key="interest.id"
              >
                {{ interest }}
                <span v-if="index != profileData.areasOfInterest.length - 1">
                  /
                </span>
              </li>
            </template>
          </ul>
        </div>
        <div
          class="tcu-profile__bio tcu-profile__push-right"
          v-html="cleanBio"
        ></div>
        <p
          class="tcu-profile__push-right tcu-profile__updated"
          v-if="profileData.updated"
        >
          Last Updated: {{ formattedDate }}
        </p>
        <p class="tcu-profile__push-right" v-if="directEditLink">
          <a
            :href="directEditLink"
            class="tcu-button _ghost | button small-only-expanded"
            target="_blank"
            >Edit Profile</a
          >
        </p>
      </template>
    </template>
  </div>
</template>
<script>
import axios from "axios";
import store from "../store";
import { formatId } from "../helpers";
import { EventBus } from "../events";
import Spinner from "./Spinner";
import DOMPurify from "dompurify";

export default {
  components: {
    spinner: Spinner
  },
  metaInfo() {
    const name = this.profileData.name;
    const filename = this.profileData.filename;
    const image = this.profileData.image;
    const updated = this.profileData.updated;
    const url =
      store.config.advanced.baseURL ||
      window.location.host + store.config.profilePath;
    return {
      title: "Profile: " + name,
      // override the parent template and just use the above title only
      titleTemplate: null,
      link: [
        { rel: "canonical", href: this.getCanonical(), vmid: "canonical" }
      ],
      meta: [
        { property: "og:title", content: "Profile: " + name, vmid: "og:title" },
        {
          property: "og:url",
          content: `https://${url}/${filename}`,
          vmid: "og:url"
        },
        {
          property: "og:image",
          content: "https://www.tcu.edu" + image,
          vmid: "og:image"
        },
        { property: "og:description", content: "", vmid: "og:description" },
        { property: "og:type", content: "profile", vmid: "og:type" },
        {
          property: "og:updated_time",
          content: updated,
          vmid: "og:updated_time"
        },
        {
          name: "twitter:url",
          content: `https://${url}/${filename}`,
          vmid: "twitter:url"
        },
        {
          name: "twitter:title",
          content: "Profile: " + name,
          vmid: "twitter:title"
        },
        {
          name: "twitter:image",
          content: "https://www.tcu.edu" + image,
          vmid: "twitter:image"
        },
        {
          name: "twitter:description",
          content: "",
          vmid: "twitter:description"
        }
      ]
    };
  },
  data() {
    return {
      error: false,
      imgLoaded: false,
      profileData: {
        name: "",
        biography: {
          "wysiwyg-text": null
        },
        hasImage: false
      }
    };
  },
  props: [
    // this component expects a prop of type object
    "selectedProfile",
    "deepLink",
    "linkInterests"
  ],
  methods: {
    closeProfile(event) {
      this.$emit("closeProfile");
    },
    setPageState() {
      if (!this.deepLink) {
        window.history.pushState(
          { userid: this.profileData.filename, view: "details" },
          "Profile Details",
          this.pageStateLink
        );
      }
    },
    viewSimilarInterests: function(interest) {
      EventBus.$emit("filterReset");
      EventBus.$emit("filterChange", {
        id: "areasOfInterest",
        filters: [formatId(interest)]
      });
      this.$emit("closeProfile");
    },
    onImgLoad() {
      this.imgLoaded = true;
    },
    getCanonical() {
      const url =
        store.config.advanced.baseURL ||
        window.location.host + store.config.profilePath;
      if (this.profileData.canonical) {
        return this.profileData.canonical.includes(".json")
          ? `https://${url}/${this.profileData.filename}`
          : this.profileData.canonical;
      } else {
        return `https://${url}/${this.profileData.filename}`;
      }
    }
  },
  computed: {
    cleanBio() {
      let wizzywig = DOMPurify.sanitize(
        this.profileData.biography["wysiwyg-text"],
        {
          ADD_TAGS: ["iframe"],
          ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling"]
        }
      );

      return wizzywig;
    },
    getProgramAffiliations() {
      let programs = [];
      this.profileData.roles.forEach(role => {
        if (role.departments) {
          role.departments.forEach(department => {
            if (department.url) {
              programs.push(department);
            }
          });
        }
      });
      return programs;
    },
    hasImage() {
      let cleanImgPath = "";
      if (
        typeof this.profileData.image == "undefined" ||
        this.profileData.image === ""
      ) {
        cleanImgPath = false;
      } else {
        cleanImgPath = "https://www.tcu.edu" + this.profileData.image;
      }
      return cleanImgPath;
    },
    pageStateLink() {
      return "view/" + this.profileData.filename;
    },
    cvLink() {
      return this.profileData.curriculumVitae.charAt(0) == "/"
        ? "https://www.tcu.edu" + this.profileData.curriculumVitae
        : this.profileData.curriculumVitae;
    },
    directEditLink() {
      let deLink =
        "https://a.cms.omniupdate.com/10?skin=oucampus&account=tcu&site=www&action=de&path=";
      let sourceLink = this.profileData.filename;
      if (typeof sourceLink !== "undefined") {
        sourceLink = `/directory/_data/${this.profileData.filename}.pcf`;
      }

      deLink = deLink + sourceLink;
      return deLink;
    },
    formattedDate() {
      let dateString = new Date(this.profileData.updated);

      return dateString.toLocaleDateString("en-US");
    },
    getSchema() {
      var schema = {
        "@context": "http://schema.org",
        "@type": "Person",
        familyName: this.profileData.familyName,
        givenName: this.profileData.givenName,
        name: this.profileData.name,
        email: this.profileData.email,
        jobTitle: this.profileData.jobTitle,
        honorificSuffix: this.profileData.honorificSuffix,
        address: [
          {
            "@type": "PostalAddress",
            streetAddress: "2800 S. University Dr.",
            addressLocality: "Fort Worth",
            addressRegion: "TX",
            postalCode: "76109"
          }
        ],
        telephone: this.profileData.telephone,
        url: window.location.href,
        sameAs: this.getCanonical()
      };
      // If a box number was entered
      if (this.profileData.tcuBox) {
        schema.address.push({
          "@type": "PostalAddress",
          postOfficeBoxNumber: this.profileData.tcuBox,
          addressLocality: "Fort Worth",
          addressRegion: "TX",
          postalCode: "76129"
        });
      }
      // If an image was entered
      if (this.hasImage) {
        schema.image = {
          "@type": "ImageObject",
          contentUrl: this.hasImage,
          description: this.profileData.name + ", " + this.profileData.jobTitle,
          name: this.profileData.name
        };
      }

      return schema;
    }
  },
  mounted() {
    if (!this.deepLink) {
      this.$refs.focusEl.focus();
      window.scrollTo(0, 0);
    }
  },
  created() {
    axios({
      method: "GET",
      url: this.selectedProfile
    }).then(
      result => {
        this.profileData = result.data;
        this.setPageState();

        // once details are rendered, init any accordions
        this.$nextTick(function() {
          window.tcuAccordions();
        });
      },
      error => {
        this.error = true;
      }
    );
  }
};
</script>



// WEBPACK FOOTER //
// src/components/ProfileDetails.vue